/**
 * Briques de formulaire communes.
 *
 * Trois écrans saisissent des tiers, des sites et des machines. Sans ces
 * briques, la même mécanique — libellé, aide, erreur, panneau latéral — serait
 * réécrite trois fois et divergerait à la première correction.
 */
import { useEffect, type ReactNode } from 'react';

/** Un champ : libellé, saisie, aide facultative. */
export function Champ({
  libelle,
  aide,
  requis,
  children,
}: {
  libelle: string;
  aide?: string;
  requis?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="champ">
      <label>
        {libelle}
        {requis && <span className="requis" title="Obligatoire"> *</span>}
      </label>
      {children}
      {aide && <p className="aide">{aide}</p>}
    </div>
  );
}

/** Deux champs côte à côte, qui repassent l'un sous l'autre si la place manque. */
export function Ligne({ children }: { children: ReactNode }) {
  return <div className="ligne-champs">{children}</div>;
}

/**
 * Panneau latéral de saisie.
 *
 * Choisi plutôt qu'une page dédiée : la liste reste visible derrière, on garde
 * son contexte, et fermer ne fait pas perdre sa place. Échap ferme, comme
 * partout ailleurs.
 */
export function Panneau({
  titre,
  sousTitre,
  onFermer,
  children,
  actions,
  large = false,
}: {
  titre: string;
  sousTitre?: string;
  onFermer: () => void;
  children: ReactNode;
  actions: ReactNode;
  /** Un volet large (70 % de l'écran) pour un travail de lecture : « Préparer les pièces ». */
  large?: boolean;
}) {
  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => {
      // Un champ qui a déjà utilisé Échap (fermer sa liste de suggestions) l'a signalé : le panneau reste ouvert.
      if (e.key === 'Escape' && !e.defaultPrevented) onFermer();
    };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [onFermer]);

  return (
    <>
      {/* Le voile intercepte les clics extérieurs : fermer par mégarde en
          cliquant dans le formulaire serait pénible. */}
      <div className="voile" onClick={onFermer} />
      <aside className={large ? 'panneau panneau-large' : 'panneau'} role="dialog" aria-label={titre}>
        <header className="panneau-entete">
          <div>
            <h2>{titre}</h2>
            {sousTitre && <p className="aide">{sousTitre}</p>}
          </div>
          <button className="secondaire discret" onClick={onFermer} aria-label="Fermer">
            ✕
          </button>
        </header>

        <div className="panneau-corps">{children}</div>

        <footer className="panneau-pied">{actions}</footer>
      </aside>
    </>
  );
}

/**
 * Message d'erreur d'un formulaire.
 *
 * `class-validator` renvoie un tableau de messages par champ ; les afficher
 * tous vaut mieux que de n'en montrer qu'un et laisser l'utilisateur corriger
 * une erreur à la fois.
 */
export function ErreurFormulaire({
  message,
  details,
}: {
  message: string | null;
  details?: readonly string[] | undefined;
}) {
  if (!message) return null;
  const liste = details && details.length > 1 ? details : null;
  // L'API rend la LISTE des messages et le premier comme message principal : l'afficher deux fois (en tête, puis
  // dans la liste) ferait croire à deux problèmes. Le premier message n'est répété que s'il est seul.
  const principalRepete = liste !== null && liste[0] === message;
  return (
    <div className="message erreur">
      {principalRepete ? `${liste.length} points à corriger :` : message}
      {liste && (
        <ul className="details-erreur">
          {liste.map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
